//@flow
import { getPjpMetricsMetadata } from 'common/redux/selectors/pjpInsightsSelectors';

import { usersMapSelector } from 'common/redux/selectors/userSelector';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { dashboardColors } from 'common/enums/ColorEnum';

import type { PjpGraphResponse } from 'common/models/internal/PjpGraphResponse';
import type { PjpMetricGraph } from 'common/models/internal/PjpMetricGraph';
import type { MetricValueDataTypeEnum } from 'common/models/flow/PjpMetricResponse';
import _ from 'lodash';
import I18nHelper from 'common/helpers/I18nHelper';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

export default class PjpChartDataBuilder {
    state: any;
    props: any;

    constructor(state: any, props: any) {
        this.state = state;
        this.props = props;
    }

    getMetricsDataForGraph = (
        metricGraph: PjpMetricGraph,
        metric: ?PjpGraphResponse
    ) => {
        if (!metric) {
            return null;
        }
        const graphQueryIndex = metricGraph.graphQueries.get(0);
        const graphQueryData = metric.metricsData.get(graphQueryIndex);
        if (!graphQueryData) {
            throw new Error('Illegal state: statisticQueryData not found!');
        }
        return graphQueryData;
    };

    getGroupByTypeForMetric = (groupBy: string, metricName: string) => {
        const metadata = getPjpMetricsMetadata(this.state, this.props);
        const groupByes = metadata.getIn([metricName, 'groupBy']);
        const matchingGroupBy = groupByes.find(
            groupByItem => groupByItem.groupBy === groupBy
        );
        if (!matchingGroupBy) {
            throw new Error(
                `Illegal State: Group By ${groupBy} not found for the metric ${metricName}`
            );
        }
        return this.convertValueDataType(matchingGroupBy.dataType);
    };

    convertValueDataType(valueDataType: MetricValueDataTypeEnum) {
        switch (valueDataType) {
            case 'DATE':
                return 'DATE';
            case 'INT':
                return 'NUMBER';
            case 'STRING':
                return 'STRING';
            default:
                return valueDataType;
        }
    }

    getMatchingGroupByFromMetadata(groupBy: string, metric: string) {
        const metadataStore = getPjpMetricsMetadata(this.state, this.props);
        const metadata = metadataStore.get(metric);
        const groupByes = metadata.groupBy;
        switch (groupBy) {
            case 'PLAN':
                return {
                    dataType: 'STRING',
                    groupBy: 'PLAN',
                    label: 'pjpAnalytics.groupBys.plan',
                    staticValues: []
                };
        }
        return groupByes.find(groupByData => groupByData.groupBy === groupBy);
    }

    getLabelForGroupBy(groupBy: string, metric: string) {
        const matchingGroupBy = this.getMatchingGroupByFromMetadata(
            groupBy,
            metric
        );
        if (matchingGroupBy) {
            const { label } = matchingGroupBy;
            return label ? label : groupBy;
        }
        throw new Error(
            `matching groupBy ${groupBy} not found for metric ${metric}`
        );
    }

    getStaticValuesForGroupBy(groupBy: string, metric: string) {
        const matchingGroupBy = this.getMatchingGroupByFromMetadata(
            groupBy,
            metric
        );
        if (matchingGroupBy) {
            return matchingGroupBy.staticValues;
        }
        throw new Error(
            `matching groupBy ${groupBy} not found for metric ${metric}`
        );
    }

    //Same as getStaticValuesForGroupBy, but values as only as array (for sorting)
    getStaticValuesOnlyForGroupBy(groupBy: string, metric: string) {
        return this.getStaticValuesForGroupBy(groupBy, metric).map(
            value => value.groupByValue
        );
    }

    getLabelForMetric(metric: string) {
        const metadataStore = getPjpMetricsMetadata(this.state, this.props);
        const metadata = metadataStore.get(metric);
        const { label } = metadata;
        const updatedLabel = nsTranslate(label);
        return label ? updatedLabel : metric;
    }

    getTeam(teamId: string) {
        const teams = teamsSelector(this.state, this.props);
        return teams.get(teamId);
    }

    getUser(userId: string) {
        const users = usersMapSelector(this.state, this.props);
        return users.get(userId);
    }

    getEntityValueForPjpData(metricDataRow, groupBys) {
        const entityValue = groupBys.find(group => {
            if (metricDataRow.get(group)) {
                return group;
            }
        });
        return entityValue;
    }

    getColorAndLabelForGroupByValue(
        value: string,
        groupBy: string,
        metric: string
    ) {
        switch (groupBy) {
            // GroupByes that needs to be custom handled
            case 'TEAM': {
                const team = this.getTeam(value);
                if (team) {
                    return { label: team.name, color: team.color };
                }
                return { label: value, color: null };
            }
            case 'USER': {
                const user = this.getUser(value);
                if (user) {
                    return {
                        label: _.startCase(user.get('name')),
                        color: null
                    };
                }
                return { label: value, color: null };
            }
            case 'PLAN': {
                return {
                    label:
                        value === 'existingPlan'
                            ? 'Existing Plan'
                            : 'Current Plan',
                    color: null
                };
            }
        }
        const matchingGroupBy = this.getMatchingGroupByFromMetadata(
            groupBy,
            metric
        );
        if (!matchingGroupBy) {
            throw new Error(
                `matching groupBy ${groupBy} not found for metric ${metric}`
            );
        }
        const { staticValues } = matchingGroupBy;
        const matchingStaticValue = staticValues.find(
            staticValue => staticValue.groupByValue === value
        );
        if (matchingStaticValue) {
            return {
                label: matchingStaticValue.label,
                color: dashboardColors[matchingStaticValue.color]
                    ? dashboardColors[matchingStaticValue.color].code
                    : dashboardColors.darkSkyBlue.code
            };
        }
        return { label: value, color: null };
    }
}
